import React, { lazy } from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AuthLayout from './layouts/Auth';
import ErrorLayout from './layouts/Error';
import DashboardLayout from './layouts/Dashboard';
import PublicLayout from './layouts/Public';
import { useParams } from 'react-router-dom';

// Lazy views
const Login = lazy(() => import('views/Login'));
const Register = lazy(() => import('views/Register'));
const ForgotPassword = lazy(() => import('views/ForgotPassword'));
const Error401 = lazy(() => import('views/Error401'));
const Error404 = lazy(() => import('views/Error404'));
const Error500 = lazy(() => import('views/Error500'));
const OverviewView = lazy(() => import('views/Overview'));
const DashboardAnalyticsView = lazy(() => import('views/DashboardAnalytics'));

// Reports
const MilkingReport = lazy(() => import('views/Reports/components/Events/components/milking/'));
const WeightReport = lazy(() => import('views/Reports/components/Events/components/weight/'));
const AIReport = lazy(() => import('views/Reports/components/Events/components/ai/'));
const CalvingReport = lazy(() => import('views/Reports/components/Events/components/calving/'));
const PDReport = lazy(() => import('views/Reports/components/Events/components/pd/'));
const SyncReport = lazy(() => import('views/Reports/components/Events/components/sync/'));
const ExitReport = lazy(() => import('views/Reports/components/Events/components/exit/'));
const BatchReports = lazy(() => import('views/Reports/components/Batches/components/home'));
const MethaneReport = lazy(() => import('views/Reports/components/Events/components/methane/'));
const AllEventsReport = lazy(() => import('views/Reports/components/Events/components/all/'));

//admin

const UnitAccess = lazy(() => import('views/Administration/components/UnitAccess'));
const OrgEdit = lazy(() => import('views/Administration/components/UnitAccess/components/Org'));
const SystemUploads = lazy(() => import('views/Administration/components/SystemUploads'));
const Profile = lazy(() => import('views/Administration/components/Profile'));
const UserAccounts = lazy(() => import('views/Administration/components/UserAccounts'));
const AddUser = lazy(() => import('views/Administration/components/UserAccounts/components/add'));
const EditUser = lazy(() => import('views/Administration/components/UserAccounts/components/edit'));
const OrgSetupView = lazy(() => import('views/Administration/components/Setup/components/Setup/components/view'));
const OrgSetupAdd = lazy(() => import('views/Administration/components/Setup/components/Setup/components/add'));
const OrgSetupEdit = lazy(() => import('views/Administration/components/Setup/components/Setup/components/edit'));

// background
const BackgroundProcessView = lazy(() => import('views/Administration/components/BackgroundProcesses/components/view'));
const BackgroundProcessEdit = lazy(() => import('views/Administration/components/BackgroundProcesses/components/edit'));

// Info & Governance
const AboutView          = lazy(() => import('views/Info/About'));
const PrivacyPolicyView  = lazy(() => import('views/Info/PrivacyPolicy'));
const DataGovernanceView = lazy(() => import('views/Info/DataGovernance'));

// settings

const Settings = lazy(() => import('views/Settings'));
const LimitsView = lazy(() => import('views/Parameters/components/Limits/components/view'));
const LimitsAdd = lazy(() => import('views/Parameters/components/Limits/components/add'));
const LimitsEdit = lazy(() => import('views/Parameters/components/Limits/components/edit'));
const LocalSettingsView = lazy(() => import('views/Parameters/components/LocalSettings/components/view'));
const LocalSettingsAdd = lazy(() => import('views/Parameters/components/LocalSettings/components/add'));
const LocalSettingsEdit = lazy(() => import('views/Parameters/components/LocalSettings/components/edit'));
const EventsMatrixView = lazy(() => import('views/Parameters/components/EventsMatrix/components/view'));
const EventsMatrixEdit = lazy(() => import('views/Parameters/components/EventsMatrix/components/edit'));
const ServiceProvidersView = lazy(() => import('views/Partners/components/ServiceProviders/components/view'));
const ServiceProvidersAdd = lazy(() => import('views/Partners/components/ServiceProviders/components/add'));
const ServiceProvidersEdit = lazy(() => import('views/Partners/components/ServiceProviders/components/edit'));
const AgentsView = lazy(() => import('views/Partners/components/Agents/components/view'));
const AgentsAdd = lazy(() => import('views/Partners/components/Agents/components/add'));
const AgentsEdit = lazy(() => import('views/Partners/components/Agents/components/edit'));

//Batch processing
const BatchProcessing = lazy(() => import('views/BatchProcessing'));

const BatchProcessingWrapper = () => {
  const { tab, uuid, batch_type } = useParams();

  useEffect(() => {
    // Force re-render or do any param-based logic
  }, [tab, uuid, batch_type]);

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <BatchProcessing
        key={`${tab}-${uuid}-${batch_type}`} // <-- this line is key
        tab={tab}
        uuid={uuid}
        batch_type={batch_type}
      />
    </React.Suspense>
  );
};


// management: animals
const Animals = lazy(() => import('views/Animals'));
const AnimalDetails = lazy(() => import('views/Animals/components/AnimalDetails'));
const AnimalComponentDetails = lazy(() => import('views/Animals/components/Details'));
const AnimalEditDetail = lazy(() => import('views/Animals/components/Details/components/edit'));
const AnimalAnalytics  = lazy(() => import('views/Animals/components/Analytics'));
const AnimalBloodline  = lazy(() => import('views/Animals/components/Bloodline'));
const CowCalendar      = lazy(() => import('views/Animals/components/CowCalendar'));


// management: farms
const Farms = lazy(() => import('views/Farms'));
const AddFarm = lazy(() => import('views/Farms/components/add'));
const EditFarm = lazy(() => import('views/Farms/components/edit'));

// management: herds
const Herds = lazy(() => import('views/Herds/components/Herds/components/view'));
const AddHerd = lazy(() => import('views/Herds/components/Herds/components/add'));
const EditHerd = lazy(() => import('views/Herds/components/Herds/components/edit'));

// management: events

const Health = lazy(() => import('views/Animals/components/Health'));

const HoofHealthAdd = lazy(() => import('views/Animals/components/Health/components/HoofHealth/components/add'));
const HoofHealth = lazy(() => import('views/Animals/components/Health/components/HoofHealth'));
const HoofHealthEdit = lazy(() => import('views/Animals/components/Health/components/HoofHealth/components/edit'));

const HoofTreatmentAdd = lazy(() => import('views/Animals/components/Health/components/HoofTreatment/components/add'));
const HoofTreatment = lazy(() => import('views/Animals/components/Health/components/HoofTreatment'));
const HoofTreatmentEdit = lazy(() => import('views/Animals/components/Health/components/HoofTreatment/components/edit'));

const VaccinationAdd = lazy(() => import('views/Animals/components/Health/components/Vaccination/components/add'));
const Vaccination = lazy(() => import('views/Animals/components/Health/components/Vaccination'));
const VaccinationEdit = lazy(() => import('views/Animals/components/Health/components/Vaccination/components/edit'));

const InjuryAdd = lazy(() => import('views/Animals/components/Health/components/Injury/components/add'));
const Injury = lazy(() => import('views/Animals/components/Health/components/Injury'));
const InjuryEdit = lazy(() => import('views/Animals/components/Health/components/Injury/components/edit'));
// Health Injury


const ParasiteInfectionAdd = lazy(() => import('views/Animals/components/Health/components/ParasiteInfection/components/add'));
const ParasiteInfectionEdit = lazy(() => import('views/Animals/components/Health/components/ParasiteInfection/components/edit'));
const ParasiteInfection = lazy(() => import('views/Animals/components/Health/components/ParasiteInfection'));

const PD = lazy(() => import('views/Animals/components/PD'));
const PDAdd = lazy(() => import('views/Animals/components/PD/components/add'));
const PDEdit = lazy(() => import('views/Animals/components/PD/components/edit'));

const Insemination = lazy(() => import('views/Animals/components/Insemination'));
const InseminationAdd = lazy(() => import('views/Animals/components/Insemination/components/add'));
const InseminationEdit = lazy(() => import('views/Animals/components/Insemination/components/edit'));

const Exit = lazy(() => import('views/Animals/components/Exits'));
const ExitAdd = lazy(() => import('views/Animals/components/Exits/components/add'));

const ExitList = lazy(() => import('views/Animals/components/ExitList'));
const ExitListDetails = lazy(() => import('views/Animals/components/ExitList/components/details'));

// Sync
const Sync = lazy(() => import('views/Animals/components/Sync'));
const SyncAdd = lazy(() => import('views/Animals/components/Sync/components/add'));
const SyncEdit = lazy(() => import('views/Animals/components/Sync/components/edit'));

// Calving
const Calving = lazy(() => import('views/Animals/components/Calving'));
const CalvingAdd = lazy(() => import('views/Animals/components/Calving/components/add'));
const CalvingEdit = lazy(() => import('views/Animals/components/Calving/components/edit'));

// Milking
const Milking = lazy(() => import('views/Animals/components/Milking'));
const MilkingAdd = lazy(() => import('views/Animals/components/Milking/components/add'));
const MilkingEdit = lazy(() => import('views/Animals/components/Milking/components/edit'));

// Weight
const Weight = lazy(() => import('views/Animals/components/Weight'));
const WeightCharts = lazy(() => import('views/Animals/components/Weight/components/charts'));
const WeightAdd = lazy(() => import('views/Animals/components/Weight/components/add'));
const WeightEdit = lazy(() => import('views/Animals/components/Weight/components/edit'));

// Hair Sampling
const HairSampling = lazy(() => import('views/Animals/components/HairSampling'));
const HairSamplingAdd = lazy(() => import('views/Animals/components/HairSampling/components/add'));
const HairSamplingEdit = lazy(() => import('views/Animals/components/HairSampling/components/edit'));

// AI Straws
const StrawView = lazy(() => import('views/Straws/components/Straw/components/view'));
const StrawAdd = lazy(() => import('views/Straws/components/Straw/components/add'));
const StrawEdit = lazy(() => import('views/Straws/components/Straw/components/edit'));

// Graduation
const GraduationView = lazy(() => import('views/Graduation/components/Graduation/components/view'));
const GraduationEdit = lazy(() => import('views/Graduation/components/Graduation/components/edit'));

const routes = [
  {
    path: '/',
    element: <Navigate to="/auth/login" replace />
  },
  {
    path: '*',
    element: <Navigate to="/errors/error-404" replace />
  }, 
  // authentication 
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'register',
        element: <Register />
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />
      },
      {
        path: '*',
        element: <Navigate to="/errors/error-404" replace />
      }
    ]
  },

  //errors
  {
    path: '/errors',
    element: <ErrorLayout />,
    children: [
      {
        path: 'error-401',
        element: <Error401 />
      },
      {
        path: 'error-404',
        element: <Error404 />
      },
      {
        path: 'error-500',
        element: <Error500 />
      },
      {
        path: '*',
        element: <Navigate to="/errors/error-404" replace />
      }
    ]
  },
  
  {
    path: '/',
    element: <DashboardLayout />,
    children: [
      {
        path: 'overview',
        element: <OverviewView />
      }
    ]
  },
  
  // Dashboards
  {
    path: '/dashboards',
    element: <DashboardLayout />,
    children: [    
      {
        path: 'analytics',
        element: <DashboardAnalyticsView />
      },  
    ]
  },  

  //management
  {
    path: '/management',
    element: <DashboardLayout />,
    children: [  
      { path: 'animals', element: <Animals /> }, 
      { path: 'animals/view/:admin', element: <Animals /> }, 
      { path: 'animals/:id', element: <Animals /> }, 
      { path: 'animals/:id/:herd', element: <Animals /> }, 
      { path: 'animals-add', element: <AnimalDetails /> },
      { path: 'details/view/:id', element: <AnimalComponentDetails /> },
      { path: 'details/edit/:id/:farm/:option', element: <AnimalEditDetail /> },
      { path: 'analytics/:id',  element: <AnimalAnalytics /> },
      { path: 'bloodline/:id',    element: <AnimalBloodline /> },
      { path: 'cow-calendar/:id', element: <CowCalendar /> },
      
      // farm
      { path: 'farms', element: <Farms /> },
      { path: 'farms/view/:admin', element: <Farms /> },
      { path: 'farms/add', element: <AddFarm /> },
      { path: 'farms/edit/:id', element: <EditFarm /> },
      { path: 'farms/edit/:id/:farm_name/:tab', element: <EditFarm /> },

      // herds
      { path: 'herds', element: <Herds /> },
      { path: 'herds/add', element: <AddHerd /> },
      { path: 'herds/edit/:id', element: <EditHerd /> }, 
      
      //events
      { path: 'health', element: <Health /> },
      { path: 'health/:tab', element: <Health /> },

      { path: 'health/hoof-health/add/:id', element: <HoofHealthAdd /> },
      { path: 'health/hoof-health/view/:id', element: <HoofHealth /> },
      { path: 'health/hoof-health/edit/:id', element: <HoofHealthEdit /> },

      { path: 'health/hoof-treatment/add/:id', element: <HoofTreatmentAdd /> },
      { path: 'health/hoof-treatment/view/:id', element: <HoofTreatment /> },
      { path: 'health/hoof-treatment/edit/:id', element: <HoofTreatmentEdit /> },

      { path: 'health/vaccination/add/:id', element: <VaccinationAdd /> },
      { path: 'health/vaccination/view/:id', element: <Vaccination /> },
      { path: 'health/vaccination/edit/:id', element: <VaccinationEdit /> },

      { path: 'health/injury/add/:id', element: <InjuryAdd /> },
      { path: 'health/injury/view/:id', element: <Injury /> },
      { path: 'health/injury/edit/:id', element: <InjuryEdit /> },   

      { path: 'health/parasite-infection/add/:id', element: <ParasiteInfectionAdd /> },
      { path: 'health/parasite-infection/edit/:id', element: <ParasiteInfectionEdit /> },
      { path: 'health/parasite-infection/view/:id', element: <ParasiteInfection /> },

      { path: 'pd/view/:id', element: <PD /> },
      { path: 'pd/add/:id', element: <PDAdd /> },
      { path: 'pd/edit/:id', element: <PDEdit /> },

      { path: 'insemination/view/:id', element: <Insemination /> },
      { path: 'insemination/add/:id', element: <InseminationAdd /> },
      { path: 'insemination/edit/:id', element: <InseminationEdit /> },

      { path: 'exit/view/:id', element: <Exit /> },
      { path: 'exit/add/:id', element: <ExitAdd /> },

      { path: 'exit-list', element: <ExitList /> },
      { path: 'exit-list/details/:id', element: <ExitListDetails /> },

      // Sync
      { path: 'sync/view/:id', element: <Sync /> },
      { path: 'sync/add/:id', element: <SyncAdd /> },
      { path: 'sync/edit/:id', element: <SyncEdit /> },

      // Calving
      { path: 'calving/view/:id', element: <Calving /> },
      { path: 'calving/add/:id', element: <CalvingAdd /> },
      { path: 'calving/edit/:id', element: <CalvingEdit /> },

      // Milking
      { path: 'milking/view/:id', element: <Milking /> },
      { path: 'milking/add/:id', element: <MilkingAdd /> },
      { path: 'milking/edit/:id', element: <MilkingEdit /> },

      // Weight
      { path: 'weight/view/:id', element: <Weight /> },
      { path: 'weight/charts/:id', element: <WeightCharts /> },
      { path: 'weight/add/:id', element: <WeightAdd /> },
      { path: 'weight/edit/:id', element: <WeightEdit /> },

      // Hair Sampling
      { path: 'hair-sampling/view/:id', element: <HairSampling /> },
      { path: 'hair-sampling/add/:id', element: <HairSamplingAdd /> },
      { path: 'hair-sampling/edit/:id', element: <HairSamplingEdit /> },

      // AI Straws
      { path: 'straws/view/:status', element: <StrawView /> },
      { path: 'straws/add', element: <StrawAdd /> },
      { path: 'straws/edit/:id', element: <StrawEdit /> },

      // Graduation
      { path: 'graduation/view/:status', element: <GraduationView /> },
      { path: 'graduation/edit/:id', element: <GraduationEdit /> },

    
    
       
    ]
  },  

  //reports
  {
    path: '/reports',
    element: <DashboardLayout />,
    children: [
      { index: true, element: <Navigate to="events/milking" replace /> },
      { path: 'events/milking', element: <MilkingReport /> },
      { path: 'events/weight', element: <WeightReport /> },
      { path: 'events/ai', element: <AIReport /> },
      { path: 'events/calving', element: <CalvingReport /> },
      { path: 'events/pd', element: <PDReport /> },
      { path: 'events/sync', element: <SyncReport /> },
      { path: 'events/exit', element: <ExitReport /> },
      { path: 'events/methane', element: <MethaneReport /> },
      { path: 'events/all', element: <AllEventsReport /> },
      { path: 'batches', element: <BatchReports /> }
    ]
  },

  //admin
  {
    path: '/administration',
    element: <DashboardLayout />,
    children: [
      { path: 'org', element: <UnitAccess /> },
      { path: 'org/edit/:id/:name', element: <OrgEdit /> },
      { path: 'upload-logo', element: <SystemUploads /> },
      { path: 'org/profile', element: <Profile /> },
      { path: 'org/user-accounts', element: <UserAccounts /> },
      { path: 'org/user-accounts/add', element: <AddUser /> },
      { path: 'org/user-accounts/edit/:id', element: <EditUser /> },
      { path: 'org/setup', element: <OrgSetupView /> },
      { path: 'org/add', element: <OrgSetupAdd /> },
      { path: 'org/edit/:id', element: <OrgSetupEdit /> }
    ]
  },

  //background processes
  {
    path: '/background-processes',
    element: <DashboardLayout />,
    children: [ 
      { path: 'org', element: <BackgroundProcessView /> },
      { path: 'org/edit/:id', element: <BackgroundProcessEdit /> }
    ]
  },

  // Info & Governance (public, no authentication required)
  {
    path: '/info',
    element: <PublicLayout />,
    children: [
      { path: 'about',           element: <AboutView /> },
      { path: 'privacy-policy',  element: <PrivacyPolicyView /> },
      { path: 'data-governance', element: <DataGovernanceView /> },
    ],
  },

  //settings
  {
    path: '/settings',
    element: <DashboardLayout />,
    children: [
      { index: true, element: <Settings /> },
      { path: ':tab', element: <Settings /> },

      {
        path: 'parameters',
        children: [
          { path: 'limits', element: <LimitsView /> },
          { path: 'limits/add', element: <LimitsAdd /> },
          { path: 'limits/edit/:id', element: <LimitsEdit /> },

          { path: 'local-settings', element: <LocalSettingsView /> },
          { path: 'local-settings/add', element: <LocalSettingsAdd /> },
          { path: 'local-settings/edit/:id', element: <LocalSettingsEdit /> },

          { path: 'events-matrix', element: <EventsMatrixView /> },
          { path: 'events-matrix/edit/:id', element: <EventsMatrixEdit /> },
        ]
      },

      {
        path: 'partners',
        children: [
          { path: 'service-providers', element: <ServiceProvidersView /> },
          { path: 'service-providers/add', element: <ServiceProvidersAdd /> },
          { path: 'service-providers/edit/:id', element: <ServiceProvidersEdit /> },

          { path: 'agents', element: <AgentsView /> },
          { path: 'agents/add', element: <AgentsAdd /> },
          { path: 'agents/edit/:id', element: <AgentsEdit /> }
        ]
      },     

    ]
  },

  

  // batch-processing
  {
    path: '/batch-processing',
    element: <DashboardLayout />, 
    children: [
      { index: true, element: <BatchProcessing /> }, // equivalent to path: ''
      { path: ':tab', element: <BatchProcessingWrapper /> },
      { path: ':tab/:uuid/:batch_type', element: <BatchProcessingWrapper /> }      
    ]
  }   
  
];

export default routes;